import React, { Dispatch, SetStateAction, useState } from 'react';

import {
  CreateUpdateContractDto,
  GetContractDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import { usePokCore } from '../../../common/hooks/usePokCore';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import Button from '../../../common/components/Button/Button';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { validate } from '../../../common/pokCore/contexts/ContractContext';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { useChangeTab } from '../../../utils/tabUtils';
import { ContractStatusEnum } from '../../../common/pokCore/validation/enums';
import { useNavigation } from '../../../common/navigation';

import { ContractTabsEnum } from './ContractTabsEnum';

interface ContractFormGeneralControlsProps {
  id?: string;
  contract: CreateUpdateContractDto;
  readOnly: boolean;
  handleRefresh: (id?: string) => void;
  setSaveErrors: Dispatch<SetStateAction<string[]>>;
}

export const ContractFormGeneralControls: React.FC<
  ContractFormGeneralControlsProps
> = props => {
  const pok = usePokCore();
  const nav = useNavigation();
  const notifications = useNotifications();
  const handleChangeTab = useChangeTab<ContractTabsEnum>();
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingDelete, setIsLoadingDelete] = useState(false);
  const id = props.id;
  const { t, tk } = useTranslation('projectLng');

  const handleGoNext = () =>
    handleChangeTab(ContractTabsEnum.ESTIMATE_ITEM_POSITIONS);

  const handleSave = async (redirect?: boolean) => {
    let createdContract: GetContractDto | undefined;
    if (id) {
      await pok.contracts.update(id, props.contract);
      if (redirect) {
        await props.handleRefresh(createdContract?.id);
        void handleGoNext();
      }
    } else {
      createdContract = await pok.contracts.create(props.contract);
    }
    if (!id && createdContract?.id && redirect) {
      await handleGoNext();
      void nav.changeContractEditorPath(
        createdContract?.id,
        ContractTabsEnum.ESTIMATE_ITEM_POSITIONS,
        createdContract?.tv,
      );
      await props.handleRefresh(createdContract?.id);
    }

    notifications.saveCompleted();
  };

  const addEditContract = async () => {
    props.setSaveErrors([]);
    const status = await validate(props.contract);
    if (status.valid) {
      try {
        setIsLoadingSave(true);
        await handleSave(true);
      } catch (response) {
        props.setSaveErrors(await responseErrors(response as ResponseError));
      } finally {
        setIsLoadingSave(false);
      }
    } else {
      props.setSaveErrors(status.errors);
    }
  };

  const handleDelete = () => {
    if (id) {
      setIsLoadingDelete(true);
      return pok.contracts
        .deactivate(id)
        .then(() => props.handleRefresh())
        .catch(async response => {
          const errors = await responseErrors(response as ResponseError);
          props.setSaveErrors(errors);
        })
        .finally(() => setIsLoadingDelete(false));
    }
  };

  const handleUpdateAfterFinish = () => {
    if (id) {
      return pok.contracts
        .markEnteredStatus(id)
        .then(() => props.handleRefresh())
        .catch(async response => {
          const errors = await responseErrors(response as ResponseError);
          props.setSaveErrors(errors);
        });
    }
  };

  return (
    <>
      <div className="side-by-side">
        <div className="left">
          {id && (
            <LastEditorInfo
              className="mt-4 color-primary"
              id={id}
              method={pok.contracts.getLastEditor}
            />
          )}
        </div>
        <div className="right">
          <>
            {props.id &&
              props.contract?.active &&
              (props.contract?.status === ContractStatusEnum.Signed ||
                props.contract?.status === ContractStatusEnum.Sent ||
                props.contract?.status ===
                  ContractStatusEnum.WaitingForSigning) && (
                <ConfirmationButton
                  variant="outline-danger"
                  className="ms-2 mt-4"
                  onOK={handleUpdateAfterFinish}
                  confirmation={t(tk.contract.askUpdateContract)}
                >
                  {t(tk.button.update)}
                </ConfirmationButton>
              )}
            {props.id &&
              props.contract?.active &&
              (props.contract?.status === ContractStatusEnum.Entered ||
                props.contract?.status === ContractStatusEnum.Unsigned) && (
                <ConfirmationButton
                  variant="outline-danger"
                  className="ms-2 mt-4"
                  onOK={handleDelete}
                  isLoading={isLoadingDelete}
                  confirmation={t(tk.contract.askDeleteContract)}
                >
                  {t(tk.button.delete)}
                </ConfirmationButton>
              )}
            <Button
              className="ms-2 mt-4"
              variant="primary"
              onClick={props.readOnly ? handleGoNext : addEditContract}
              isLoading={isLoadingSave}
            >
              {props.readOnly
                ? t(tk.contract.stepForward)
                : t(tk.contract.saveAndStepForward)}
            </Button>
          </>
        </div>
      </div>
    </>
  );
};
